import React, { useState, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ApexStackedColumnV2 from "../../../Charts/Column/ApexStackedColumnV2";
import StateOfUseTrendDetailsSidebar from "./StateOfUseTrendDetailsSidebar";
import StateOfUseTrendDetailsTableSection from "./StateOfUseTrendDetailsTableSection";

// ─── Chart constants (same as StateOfUseTrendChart) ───────────────────────────
const LEGEND_ITEMS = [
  { name: "Discharging", color: "#1565C0" },
  { name: "Charging",    color: "#2DB55D" },
  { name: "Offline",     color: "#F48989" },
];
const SERIES_COLORS = ["#F48989", "#2DB55D", "#1565C0"];

// ─── Granularity rules (mirrors StateOfUseTrendChart) ────────────────────────
const getGranularityForDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "weekly";
  const diff = dayjs(endDate).diff(dayjs(startDate), "day")+1;
  if (diff <= 14) return "daily";
  if (diff <= 60) return "weekly";
  return "monthly";
};

const VALUE_PATTERN = [
  { discharging: 55, charging: 35, offline: 10 },
  { discharging: 50, charging: 40, offline: 7  },
  { discharging: 48, charging: 35, offline: 9  },
  { discharging: 52, charging: 38, offline: 8  },
  { discharging: 58, charging: 32, offline: 9  },
  { discharging: 45, charging: 42, offline: 8  },
  { discharging: 60, charging: 30, offline: 8  },
  { discharging: 47, charging: 49, offline: 8  },
];

const generateTempTrendData = (startDate, endDate) => {
  const granularity = getGranularityForDateRange(startDate, endDate);
  const start = dayjs(startDate);
  const end   = dayjs(endDate);
  const entries = [];
  let i = 0;

  if (granularity === "daily") {
    let cur = start;
    while (cur.diff(end, "day") <= 0) {
      entries.push({ period: cur.format("D MMM"), ...VALUE_PATTERN[i % VALUE_PATTERN.length] });
      cur = cur.add(1, "day");
      i++;
    }
  } else if (granularity === "weekly") {
    let cur = start;
    while (cur.diff(end, "day") <= 0) {
      const potentialEnd = cur.add(6, "day");
      const weekEnd = potentialEnd.diff(end, "day") > 0 ? end : potentialEnd;
      entries.push({ period: `${cur.format("D MMM")} - ${weekEnd.format("D MMM")}`, ...VALUE_PATTERN[i % VALUE_PATTERN.length] });
      cur = cur.add(7, "day");
      i++;
    }
  } else {
    // monthly
    const endMonth = end.startOf("month");
    let cur = start.startOf("month");
    while (cur.diff(endMonth, "day") <= 0) {
      entries.push({ period: cur.format("MMM YYYY"), ...VALUE_PATTERN[i % VALUE_PATTERN.length] });
      cur = cur.add(1, "month");
      i++;
    }
  }

  return entries;
};

// ─── Component ────────────────────────────────────────────────────────────────
const StateOfUseTrendDetailsView = ({
  onBack,
  startDate: initialStartDate,
  endDate: initialEndDate,
  allOnboardingLocationsHierarchy,
  initialHierarchicalLocation,
}) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [granularity, setGranularity] = useState("Weekly");
  const [hierarchicalLocation, setHierarchicalLocation] = useState(initialHierarchicalLocation || []);
  const [operationalStatus, setOperationalStatus] = useState(["operational", "non_operational"]);

  // Re-generate data whenever the date range changes
  const tempData = useMemo(() => generateTempTrendData(startDate, endDate), [startDate, endDate]);

  const categories = useMemo(() => tempData.map((d) => d.period), [tempData]);

  const series = useMemo(() => [
    { name: "Offline",     data: tempData.map((d) => d.offline) },
    { name: "Charging",    data: tempData.map((d) => d.charging) },
    { name: "Discharging", data: tempData.map((d) => d.discharging) },
  ], [tempData]);

  const handleDateChange = useCallback(({ startDate: s, endDate: e }) => {
    setStartDate(s);
    setEndDate(e);
  }, []);

  return (
    <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative" }}>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: isSidebarExpanded ? "calc(100% - 320px)" : "100%",
          minWidth: 0,
          transition: "width 0.3s ease-in-out",
        }}
      >
        {/* Back button */}
        <Box
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            width: "fit-content",
            "&:hover": {
              "& .MuiTypography-root": { color: "#6D72F6 !important" },
              "& .MuiSvgIcon-root": { color: "#6D72F6" },
            },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
          <Typography variant="fw500s14lh24c2E2E2E">Back</Typography>
        </Box>

        {/* Chart card */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "24px 28px",
            border: "1px solid #E6E6E6",
          }}
        >
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E", mb: 1, display: "block" }}>
            State-of-Use Trend
          </Typography>
          <ApexStackedColumnV2
            chartId="state-of-use-trend-details-chart"
            series={series}
            categories={categories}
            colors={SERIES_COLORS}
            height={315}
            columnWidth="45%"
            yMin={0}
            yMax={100}
            yTickAmount={5}
            yAxisTitle="Distribution (%)"
            tooltipYFormatter={(val) => `${val}%`}
            legendItems={LEGEND_ITEMS}
            legendJustify="center"
          />
        </Box>

        {/* Vehicle list table */}
        <StateOfUseTrendDetailsTableSection
          startDate={startDate}
          endDate={endDate}
          hierarchicalLocation={hierarchicalLocation}
          isOpen={true}
        />
      </Box>

      {/* ── Settings sidebar ──────────────────────────────────────────────── */}
      <StateOfUseTrendDetailsSidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded((v) => !v)}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        granularity={granularity}
        onGranularityChange={setGranularity}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        selectedLocations={hierarchicalLocation}
        onLocationChange={setHierarchicalLocation}
        selectedOperationalStatus={operationalStatus}
        onOperationalStatusChange={setOperationalStatus}
      />
    </Box>
  );
};

export default StateOfUseTrendDetailsView;
